import { useMemo, useState } from "react";
import { User, Users } from "lucide-react";
import { buildToday, FILTERS } from "@/components/mobile/todayFeed";
import { plur } from "@/lib/format";
import { HomePrefsButton, useHomePrefs } from "@/components/home/HomePrefs";
import { useSlidingPill } from "@/components/shared/Pill";

// Karty pod pruhem úvodu na počítači (236): Dnes · Plán na zítřek · Termíny do 14 dní.

const DOT = { dochazka: "hsl(var(--success))", doklady: "hsl(var(--accent-orange))", objednavky: "#3b82c4", denik: "#6b7bd6", ukoly: "hsl(var(--foreground))" };
const KIND = { dochazka: "Docházka", doklady: "Doklad", objednavky: "Objednávka", denik: "Deník", ukoly: "Úkol" };
const TONE = { danger: "text-destructive", ok: "text-success", brand: "pp-ink", muted: "text-muted-foreground" };
const DOW = ["neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota"];
const ymdDate = (s) => { const [y, m, d] = String(s || "").slice(0, 10).split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
export const dayTitle = (s) => { const d = ymdDate(s); return `${DOW[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.`; };
const ini = (name) => String(name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

export function Card({ children, testid, className = "" }) {
  return <section className={`bg-card border border-border rounded-[22px] p-5 min-w-0 ${className}`} data-testid={testid}>{children}</section>;
}
export function CardHead({ title, children }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-1">
      <h2 className="font-heading text-lg font-bold tracking-tight truncate">{title}</h2>
      {children && <div className="flex items-center gap-2 shrink-0">{children}</div>}
    </div>
  );
}
const linkCls = "text-[13.5px] font-bold pp-ink hover:underline underline-offset-4 whitespace-nowrap";

// ---------------- Dnes ----------------
// 258: dělník má místo „Já / Firma“ přepínač Dnes / Zítra (`tomorrow` = data zítřka ve stejném tvaru
// jako /dashboard/today, jen úkoly) — zítřek bez filtrů a bez čáry „Teď“.
export function TodayCard({ data, scope, onScope, showScope, managerScope, nowMin, meId, onOpen, tomorrow, prefsCards = false }) {
  const { off } = useHomePrefs();   // 330: Můj Přehled
  const [filter, setFilter] = useState("vse");
  const [day, setDay] = useState("today");
  // 385: podklad vybrané volby dojede s pružinou (den, čí záznamy, filtr osy)
  const dayPill = useSlidingPill(day);
  const scopePill = useSlidingPill(scope);
  const [showAll, setShowAll] = useState(false);
  const tmr = !!tomorrow && day === "tomorrow";
  const src = tmr ? tomorrow.data : data;
  const feed = useMemo(() => buildToday(src, tmr ? { filter: "vse", showAll: true, nowMin: -1, meId, limit: 99, off } : { filter, showAll, nowMin, meId, limit: 8, off }),
    [src, tmr, filter, showAll, nowMin, meId, off]);
  const filterPill = useSlidingPill(feed.filter, `${tmr}|${Object.values(feed.counts || {}).join(",")}`);
  const rows = !src ? [] : tmr ? feed.ups.map((e) => (e.t === "dnes" ? { ...e, t: "den" } : e))
    : [...feed.past, { now: true }, ...feed.live, ...feed.ups];   // 251: nahoře co bylo, dole co čeká
  const seg = (k, label, Icon) => (
    <button type="button" aria-pressed={scope === k} onClick={() => onScope(k)} title={label} aria-label={label} data-testid={`today-scope-${k}`} data-pill-on={scope === k || undefined}
      className={`relative z-[1] w-9 h-[30px] rounded-[9px] grid place-items-center transition-colors ${scope === k ? `${scopePill.ready ? "" : "bg-card shadow-sm"} text-foreground` : "text-muted-foreground hover:text-foreground"}`}>
      <Icon size={16} aria-hidden="true" />
    </button>
  );
  return (
    <Card testid="home-today">
      <CardHead title={tmr ? `Zítra · ${tomorrow.label}` : "Dnes"}>
        {tomorrow && (
          <div ref={dayPill.ref} role="group" aria-label="Den" className="relative inline-flex gap-0.5 p-[3px] rounded-[12px] bg-secondary" data-testid="today-day">
            {dayPill.pill("bg-card shadow-sm !rounded-[9px]")}
            {[["today", "Dnes"], ["tomorrow", "Zítra"]].map(([k, l]) => (
              <button key={k} type="button" aria-pressed={day === k} onClick={() => { setDay(k); setShowAll(false); }} data-testid={`today-day-${k}`} data-pill-on={day === k || undefined}
                className={`relative z-[1] h-[30px] px-3.5 rounded-[9px] text-[13.5px] font-bold transition-colors ${day === k ? `${dayPill.ready ? "" : "bg-card shadow-sm"} text-foreground` : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        )}
        {showScope && (
          <div ref={scopePill.ref} role="group" aria-label="Čí záznamy" className="relative inline-flex gap-0.5 p-[3px] rounded-[12px] bg-secondary">
            {scopePill.pill("bg-card shadow-sm !rounded-[9px]")}
            {seg("me", "Jen moje", User)}{seg("firm", managerScope ? "Moje zakázky" : "Celá firma", Users)}
          </div>
        )}
        <HomePrefsButton cards={prefsCards} className="-mr-1.5" />
      </CardHead>
      {!tmr && <div ref={filterPill.ref} role="group" aria-label="Co ukázat" className="relative flex gap-1.5 flex-wrap mt-2 mb-1.5" data-testid="today-filters">
        {filterPill.pill("bg-foreground")}
        {FILTERS.filter(([k]) => !off.includes(k)).map(([k, label]) => (
          <button key={k} type="button" aria-pressed={feed.filter === k} onClick={() => { setFilter(k); setShowAll(false); }} data-testid={`today-filter-${k}`} data-pill-on={feed.filter === k || undefined}
            className={`relative z-[1] h-[34px] px-3.5 rounded-full text-[13.5px] font-bold inline-flex items-center gap-1.5 border transition-colors ${feed.filter === k ? `${filterPill.ready ? "border-transparent" : "bg-foreground border-foreground"} text-background` : "border-border hover:bg-secondary"}`}>
            {label}
            {feed.counts?.[k] > 0 && (
              <span className={`min-w-[22px] h-[22px] px-1.5 rounded-full text-[12px] font-bold tabular-nums inline-flex items-center justify-center ${feed.filter === k ? "bg-background/20" : "bg-secondary"}`}
                data-testid={`today-count-${k}`} aria-label={`${feed.counts[k]} nedokončených`}>{feed.counts[k]}</span>
            )}
          </button>
        ))}
      </div>}
      {!src ? (
        <div className="space-y-2 mt-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-11 rounded-[12px] bg-secondary animate-pulse motion-reduce:animate-none" />)}</div>
      ) : (
        <div data-testid="today-feed">
          {!tmr && (feed.hidden > 0 || feed.canCollapse) && (
            <button type="button" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll} data-testid="today-more"
              className="mb-1 h-10 w-full rounded-[12px] text-sm font-bold pp-ink hover:bg-secondary/60">
              {showAll ? "Sbalit" : `Ukázat celý den (+${feed.hidden})`}
            </button>
          )}
          {rows.map((e) => e.now ? (
            <div key="now" className="grid grid-cols-[52px_14px_minmax(0,1fr)] gap-x-2.5 items-center my-0.5" role="note" aria-label={`Teď ${feed.now}`}>
              <span className="text-[12.5px] font-extrabold pp-ink">Teď</span>
              <span className="col-span-2 flex items-center gap-2"><span className="flex-1 h-0.5 rounded-full bg-brand" /><span className="text-xs font-bold tabular-nums pp-ink">{feed.now}</span></span>
            </div>
          ) : (
            <button key={e.key} type="button" onClick={() => onOpen(e, setFilter)} data-testid={`today-row-${e.key}`}
              className={`w-[calc(100%+1.25rem)] -mx-2.5 px-2.5 grid grid-cols-[52px_14px_minmax(0,1fr)_auto] gap-x-2.5 items-start py-2.5 border-b border-border last:border-b-0 text-left rounded-[10px] hover:bg-secondary/50 ${e.up && !e.late ? "opacity-80" : ""}`}>
              <span className={`text-[13.5px] tabular-nums pt-px ${e.up ? "text-foreground font-semibold" : "text-muted-foreground"}`}>{e.t}</span>
              <span aria-hidden="true" className="mt-[5px] w-2.5 h-2.5 rounded-full" style={e.kind === "ukoly" && e.up
                ? { boxShadow: `inset 0 0 0 2px ${DOT.ukoly}` } : { background: e.live ? DOT.dochazka : DOT[e.kind] || DOT.ukoly }} />
              <span className="min-w-0">
                <span className="block text-[14.5px] font-bold truncate">{e.title}</span>
                <span className="block text-[13px] text-muted-foreground truncate">{e.sub}</span>
              </span>
              <span className="flex flex-col items-end gap-1 whitespace-nowrap">
                {feed.filter === "vse" && <span className="text-[11.5px] font-bold px-2 py-0.5 rounded-lg bg-secondary text-muted-foreground">{KIND[e.kind]}</span>}
                {e.amt ? <span className={`text-[13.5px] font-bold tabular-nums ${e.amtTone === "ok" ? "text-success" : ""}`}>{e.amt}</span> : null}
                {e.pay ? <span className={`text-xs font-semibold ${TONE[e.payTone] || TONE.muted}`}>{e.pay}</span> : null}
              </span>
            </button>
          ))}
          {tmr ? (!rows.length && <p className="text-sm text-muted-foreground py-3" data-testid="today-empty">{tomorrow.empty}</p>)
            : feed.empty && <p className="text-sm text-muted-foreground py-3 pl-[76px]" data-testid="today-empty">{feed.empty}</p>}
        </div>
      )}
    </Card>
  );
}

// ---------------- Plán na zítřek ----------------
export function TomorrowCard({ plan, onOpenTasks, onOpenProject, onNewTask }) {
  if (!plan) return <Card testid="home-tomorrow"><div className="h-40 rounded-[14px] bg-secondary animate-pulse motion-reduce:animate-none" /></Card>;
  // Jen stavby, kde má někdo na zítra úkol — běžící zakázky bez lidí se nezobrazují (238)
  const projects = (plan.projects || []).filter((g) => g.people?.length), idle = plan.idle || [];
  const title = plan.isNextDay ? "Plán na zítřek" : `Plán na ${DOW[ymdDate(plan.date).getDay()]}`;
  return (
    <Card testid="home-tomorrow">
      <CardHead title={title}><button type="button" onClick={onOpenTasks} className={linkCls} data-testid="tomorrow-open-tasks">Otevřít v Úkolech</button></CardHead>
      <p className="text-[13px] text-muted-foreground mb-1.5">{dayTitle(plan.date)} · podle úkolů na {plan.isNextDay ? "zítra" : "ten den"}</p>
      {projects.length === 0 && idle.length === 0 && <p className="text-sm text-muted-foreground py-2">Zatím nic naplánováno.</p>}
      {projects.map((g) => (
        <button key={g.projectId || "none"} type="button" onClick={() => onOpenProject(g)} data-testid={`tomorrow-project-${g.projectId || "none"}`}
          className="w-[calc(100%+1.25rem)] -mx-2.5 px-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center py-2.5 border-b border-border last:border-b-0 text-left rounded-[10px] hover:bg-secondary/50">
          <span className="min-w-0">
            <span className="block text-[14.5px] font-bold truncate">{g.projectName}</span>
            <span className="block text-[12.5px] text-muted-foreground truncate">{g.people.length ? g.people.map((p) => p.name).join(", ") : "Na zítra tu nikdo nemá úkol"}</span>
          </span>
          {g.people.length ? (
            <span className="flex">
              {g.people.slice(0, 4).map((p, i) => (
                <span key={p.id} title={p.name} className="relative w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold tracking-tight bg-card text-success ring-2 ring-card"
                  style={{ marginLeft: i ? -4 : 0, zIndex: 5 - i, backgroundImage: "linear-gradient(hsl(var(--success) / .15), hsl(var(--success) / .15))" }}>{p.initials || ini(p.name)}</span>
              ))}
              {g.people.length > 4 && <span className="w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold bg-secondary text-muted-foreground ring-2 ring-card" style={{ marginLeft: -4 }}>+{g.people.length - 4}</span>}
            </span>
          ) : (
            <span className="text-[11px] font-extrabold uppercase tracking-[.05em] px-2 py-1 rounded-lg bg-destructive/10 text-destructive">Nikdo</span>
          )}
        </button>
      ))}
      {idle.length > 0 && (
        <div className="mt-2.5 flex items-center justify-between gap-2.5 flex-wrap rounded-[14px] bg-destructive/10 px-3.5 py-2.5" data-testid="tomorrow-idle">
          <span className="text-[13.5px] min-w-0">
            <span className="font-bold text-destructive">Na {plan.isNextDay ? "zítra" : "ten den"} nemá nic: </span>
            <span className="text-foreground/80">{idle.slice(0, 8).map((p) => p.name).join(", ")}{idle.length > 8 ? ` a dalších ${idle.length - 8}` : ""}</span>
          </span>
          <button type="button" onClick={() => onNewTask(idle.slice(0, 8).map((p) => p.id))} data-testid="tomorrow-new-task"
            className="h-9 px-3.5 rounded-full bg-card border border-border text-[13px] font-bold text-destructive whitespace-nowrap w-full sm:w-auto">+ Zadat úkol na {plan.isNextDay ? "zítra" : "ten den"}</button>
        </div>
      )}
    </Card>
  );
}

// ---------------- Termíny do 14 dní ----------------
export function DeadlinesCard({ items, onOpen }) {
  const list = items || [];
  const rel = (n) => (n <= 0 ? "dnes" : n === 1 ? "zítra" : `za ${plur(n, "den", "dny", "dní")}`);
  return (
    <Card testid="home-deadlines">
      <CardHead title="Termíny do 14 dní"><button type="button" onClick={() => onOpen()} className={linkCls}>Harmonogram</button></CardHead>
      {items == null ? <div className="h-40 rounded-[14px] bg-secondary animate-pulse motion-reduce:animate-none mt-2" />
        : list.length === 0 ? <p className="text-sm text-muted-foreground py-2">Žádné blížící se termíny.</p>
        : list.slice(0, 7).map((p, i) => {
          const d = ymdDate(p.date), hot = p.inDays <= 1;
          return (
            <button key={i} type="button" onClick={() => onOpen(p)} data-testid={`deadline-row-${i}`}
              className="w-[calc(100%+1.25rem)] -mx-2.5 px-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-3.5 items-center py-2.5 border-b border-border last:border-b-0 text-left rounded-[10px] hover:bg-secondary/50">
              <span className="min-w-0">
                <span className="block text-sm font-bold truncate">{p.title}</span>
                <span className="block text-[12.5px] text-muted-foreground truncate">{p.projectName || "—"}</span>
              </span>
              <span className={`text-right whitespace-nowrap tabular-nums leading-tight ${hot ? "pp-ink" : ""}`}>
                <span className="block text-sm font-bold">{d.getDate()}. {d.getMonth() + 1}.</span>
                <span className={`block text-[12.5px] ${hot ? "font-bold" : "text-muted-foreground"}`}>{rel(p.inDays)}</span>
              </span>
            </button>
          );
        })}
      {list.length > 7 && <button type="button" onClick={() => onOpen()} className={`${linkCls} mt-2`}>Další v harmonogramu ({list.length - 7})</button>}
    </Card>
  );
}
